
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { usePermissions } from "@/hooks/usePermissions";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { NavigationItem } from "@/hooks/useNavigationItems";

export interface NavItemProps {
  item: NavigationItem;
  active?: boolean;
  onClick?: () => void;
  onToggleExpand?: () => void;
  onHover?: () => void;
  collapsed?: boolean;
  badge?: string;
  isExpanded?: boolean;
}

export const NavItem = ({ 
  item,
  active, 
  onClick, 
  onToggleExpand,
  onHover, 
  collapsed, 
  badge,
  isExpanded
}: NavItemProps) => {
  const { hasPermission } = usePermissions();
  const hasChildren = !!item.children && item.children.length > 0;
  
  // Don't render the item if permission is required but not granted
  if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
    return null;
  }
  
  return (
    <div className="mb-1">
      <Button
        variant="ghost"
        className={cn(
          "w-full justify-start relative transition-all duration-200",
          "text-slate-600 hover:text-slate-900 hover:bg-slate-100/80",
          "focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2",
          active && [
            "bg-slate-100 text-slate-900 font-medium",
            "before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-primary",
            "hover:bg-slate-100"
          ],
          hasChildren && "pr-10", // Extra padding for the chevron
          collapsed ? "px-3" : "px-4",
        )}
        onClick={() => {
          if (hasChildren && onToggleExpand) {
            onToggleExpand();
          } else if (onClick) {
            onClick();
          }
        }}
        onMouseEnter={onHover}
      >
        <motion.div 
          className="flex items-center gap-3"
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <span className={cn(
            "text-slate-500 transition-colors",
            active && "text-primary"
          )}>
            {item.icon}
          </span>
          {!collapsed && (
            <span className="flex-1 transition-all">
              {item.label}
            </span>
          )}
          {!collapsed && badge && (
            <Badge variant="purple" size="sm" className="ml-auto">
              {badge}
            </Badge>
          )}
        </motion.div>
        
        {hasChildren && !collapsed && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        )}
        
        {collapsed && badge && (
          <Badge variant="purple" size="sm" className="absolute -right-1 -top-1">
            {badge}
          </Badge>
        )}
      </Button>
    </div>
  );
};

export interface NavItemChildProps {
  item: NavigationItem;
  active?: boolean;
  onClick?: () => void;
  onHover?: () => void;
  collapsed?: boolean;
}

export const NavItemChild = ({ 
  item, 
  active, 
  onClick, 
  onHover, 
  collapsed 
}: NavItemChildProps) => {
  const { hasPermission } = usePermissions();
  
  // Don't render the item if permission is required but not granted
  if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
    return null;
  }
  
  if (collapsed) {
    return null;
  }
  
  return (
    <Button
      variant="ghost"
      className={cn(
        "w-full justify-start ml-5 pl-6 border-l border-slate-200 relative transition-all duration-200",
        "text-slate-600 hover:text-slate-900 hover:bg-slate-100/80",
        "focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2",
        active && [
          "bg-slate-100 text-slate-900 font-medium",
          "before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-primary",
          "hover:bg-slate-100"
        ],
      )}
      onClick={onClick}
      onMouseEnter={onHover}
    >
      <motion.div 
        className="flex items-center gap-3"
        initial={{ opacity: 0.8 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <span className={cn(
          "text-slate-500 transition-colors",
          active && "text-primary"
        )}>
          {item.icon}
        </span>
        <span className="flex-1 transition-all">
          {item.label}
        </span>
      </motion.div>
    </Button>
  );
};
